import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCopy, faEye, faEyeSlash, faLock, faPencil, faTrashAlt, faUnlock,
} from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faPlusCircle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Button, Form, Grid, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { deepSetObject, digObject, isValidPrice } from '@domain-group/javascript-utils'

import useCampaignQuoteTemplateItem, { useCampaignQuoteTemplateItemForm } from '@hooks/useCampaignQuoteTemplateItem'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CustomItemSearch from '@components/CustomItemSearch'
import EntitySelector from '@components/EntitySelector'

import {
  defaultRequestOptions,
} from '@components/QuoteBuilder/components/QuoteTemplateBuilder/hooks/useQuoteTemplateBuilder'

const MODAL_KEY = 'CreateOrEditCampaignQuoteTemplateItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => void,
  duplicateCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => void,
  updateCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignQuoteTemplateItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal,
    createCampaignQuoteTemplateItem,
    duplicateCampaignQuoteTemplateItem,
    updateCampaignQuoteTemplateItem,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const initCampaignQuoteTemplateItem = digObject(modalPayload, 'campaignQuoteTemplateItem', {})

  const newRecord = !initCampaignQuoteTemplateItem.id

  const campaignQuoteTemplateItemPayload = useCampaignQuoteTemplateItem(initCampaignQuoteTemplateItem, {
    requestOptions: { include_campaign_quote_template_publication: true },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      createCampaignQuoteTemplateItem: createFn,
      deleteCampaignQuoteTemplateItem: deleteFn,
      duplicateCampaignQuoteTemplateItem: duplicateFn,
      editQuantityAndOptions,
      updateCampaignQuoteTemplateItem: updateFn,
    },
    campaignQuoteTemplateItem,
    campaignQuoteTemplateItem: {
      compulsory: isCompulsory,
      item_type: itemType,
      package_id,
    },
    creating,
    updating,
  } = campaignQuoteTemplateItemPayload

  const {
    entityState,
    entityState: {
      data: {
        hidden,
      },
      description,
      display_title,
      internal_notes,
      item_type,
      locked,
      price,
      price_type,
      provider_id,
      required_days_from_start,
      row_title,
      selected,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useCampaignQuoteTemplateItemForm(campaignQuoteTemplateItem, { validateOn: 'change' })

  const createOrUpdateQuoteTemplateItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: { ...defaultRequestOptions.campaignQuoteTemplateItem },
    toastText: `Quote Template Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  const duplicateQuoteTemplateItemPayload = {
    callbacks: {
      action: duplicateFn,
      afterAction: closeModal,
      buildActionParams: () => [{ ...defaultRequestOptions.campaignQuoteTemplateItem }],
    },
    toastText: 'Quote Template Item Duplicated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Template Item"
      />

      <SidebarModal.Content>
        <Form>
          {!!provider_id && (
            // @ts-expect-error TS2741
            <EntitySelector
              boxProps={{ marginBottom: 'large' }}
              disabled
              entityKey="providers"
              label="* Provider"
              selectedItemId={provider_id}
              showEntityId
            />
          )}

          <Form.Field label="* Title" errorMessage={errors.display_title}>
            {newRecord && campaignQuoteTemplateItem.item_type === 'custom' && (
              <CustomItemSearch
                callbacks={{
                  onChange: value => setEntityState({ title: value }),
                  selectItem: item => setEntityState({
                    custom_item_id: item.id,
                    description: item.description,
                    title: item.title,
                    price: item.price,
                    price_override: isValidPrice(item.price_override) ? item.price_override : item.price,
                    price_type: 'fixed',
                  }),
                }}
                value={title}
              />
            )}

            {!newRecord && (
              <input name="display_title" type="text" value={display_title} {...handlers} />
            )}
          </Form.Field>

          <Form.Field label="Item Description" errorMessage={errors.description} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="description" value={description} style={{ height: 80 }} {...handlers} />
          </Form.Field>

          <Form.Field label="Internal Notes" errorMessage={errors.internal_notes} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="internal_notes" value={internal_notes} style={{ height: 80 }} {...handlers} />
          </Form.Field>

          <Form.Row>
            <Form.Field label="Due (Days from Start)" errorMessage={errors.required_days_from_start} marginTop="large">
              <input name="required_days_from_start" type="number" value={required_days_from_start} {...handlers} />
            </Form.Field>

            <Form.Field label="Row Title" errorMessage={errors.row_title} marginTop="large">
              <input name="row_title" type="text" value={row_title} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field label="Price Type" errorMessage={errors.price_type} marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select name="price_type" value={price_type} {...handlers}>
                <option value="fixed">Fixed</option>
                <option value="percentage">Percentage</option>
              </select>
            </Form.Field>

            <Form.Field
              label={price_type === 'fixed' ? 'Price' : 'Value'}
              errorMessage={errors.price}
              marginTop="large"
            >
              <input
                name="price"
                value={price}
                {...handlers}
                onChange={e => setEntityState({
                  price: e.target.value,
                  price_override: e.target.value,
                })}
              />
            </Form.Field>
          </Form.Row>

          <Form.Field label="Status" marginTop="large">
            <Box>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={selected ? faCheckCircle : faPlusCircle} />}
                onClick={() => setEntityState({ selected: !selected })}
                size="medium"
                style={{ color: selected ? '#43ac6a' : undefined, marginRight: 8 }}
                // @ts-expect-error
                width="calc(100% / 2 - 4px)"
              >
                {selected ? 'Selected' : 'Not Selected'}
              </Button>

              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={locked ? faLock : faUnlock} />}
                onClick={() => setEntityState({ locked: !locked })}
                size="medium"
                // @ts-expect-error
                width="calc(100% / 2 - 4px)"
              >
                {locked ? 'Required' : 'Optional'}
              </Button>
            </Box>
          </Form.Field>

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <Grid gridRowGap="medium">
                {itemType === 'orderable' && !package_id && (
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faPencil} />}
                    onClick={() => editQuantityAndOptions()}
                    size="medium"
                  >
                    Edit Qty & Options
                  </Button>
                )}

                {!isCompulsory && (
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={hidden ? faEye : faEyeSlash} />}
                    onClick={() => {
                      const updatedState = deepSetObject(entityState, 'data.hidden', !hidden)
                      // @ts-expect-error TS2345
                      setEntityState(updatedState)
                    }}
                    size="medium"
                  >
                    {hidden ? 'Show' : 'Hide'} Item on Quote
                  </Button>
                )}

                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  // @ts-expect-error TS2345
                  onClick={() => duplicateCampaignQuoteTemplateItem(duplicateQuoteTemplateItemPayload)}
                  size="medium"
                >
                  Duplicate Item
                </Button>

                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  onClick={() => confirmDelete({
                    callbacks: {
                      afterAction: closeModal,
                      deleteFn,
                    },
                    strings: {
                      entityName: 'Quote Template Item',
                    },
                  })}
                  size="medium"
                >
                  Delete Item
                </Button>
              </Grid>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignQuoteTemplateItem(createOrUpdateQuoteTemplateItemPayload)
              : () => updateCampaignQuoteTemplateItem(createOrUpdateQuoteTemplateItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Quote Template Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignQuoteTemplateItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
